import { observer } from 'mobx-react-lite'
import { Fade, Tooltip, Typography, useTheme } from '@mui/material'
import { AppBox } from 'src/component/common/analog/molecule/AppBox'
import { useSkillAssessmentStore } from '../../skill-assessment/store/skill-assessment.store'
import { useEffect } from 'react'
import { SkillCategory, SkillSet } from '../../skill-assessment/model/skill-assessment-model'
import { useLocalizationStore } from 'src/feature/localization/store/localization.store'
import { AppPaper } from 'src/component/common/analog/molecule/AppPaper'
import { AppSkeleton } from 'src/component/common/analog/molecule/AppSkelton'
import { Caption } from 'src/feature/localization/component/atom/Caption'
import { Word } from 'src/feature/localization/component/atom/Word'
import { generateUniqId } from 'src/model/Id'
import { AppButton } from 'src/component/common/analog/molecule/button/AppButton'
import { validateUserSkillAssessment, resetUserSkill, validateAllUserSkills, resetAllUserSkills } from 'src/feature/skill-assessment/api/skill-assessment-api'
import { ColorTokens, Palette } from 'src/feature/theme/model/Palette'
import StarsRoundedIcon from '@mui/icons-material/StarsRounded'
import VerifiedRoundedIcon from '@mui/icons-material/VerifiedRounded'

export type UserSkillGapAnalysisProps = {
  userId: string
}

export const UserSkillGapAnalysis = observer(({ userId }: UserSkillGapAnalysisProps) => {
  const theme = useTheme()
  const { translation } = useLocalizationStore()
  const skillAssessmentStore = useSkillAssessmentStore()

  const translations = translation.pages.myTeam
  const { isLoading } = skillAssessmentStore.getGenericFieldsState('getSkillSets')

  useEffect(() => {
    skillAssessmentStore.reset()
    skillAssessmentStore.getSkillSets(userId)
  }, [userId, skillAssessmentStore])

  // Get the category the manager has permission to validate
  const validatableCategory = skillAssessmentStore.skillSets?.find(cat => cat.validateFlag)

  const checkIfAllSkillsAreRated = () => {
    if (!skillAssessmentStore.skillSets || skillAssessmentStore.skillSets.length === 0) {
      return false
    }

    return skillAssessmentStore.skillSets.every(category => category.skills?.every((skill: SkillSet) => skill.selectedSetLevel !== null) ?? false)
  }

  const getSegmentColor = (userRating: number, expectedRating: number, index: number, isValidated: boolean) => {
    if (userRating && index <= userRating) {
      return isValidated ? { backgroundColor: theme.palette.primary.main } : { border: `2px solid ${theme.palette.primary.main}` }
    }
    if (expectedRating && index <= expectedRating) {
      return isValidated ? { backgroundColor: `${theme.palette.primary.main}33` } : { border: `2px solid ${theme.palette.primary.main}33` }
    }

    return { backgroundColor: theme.palette.background.dark }
  }

  // Validate a single skill assessment, only allowed for skills from the current role
  const validate = async (skill: SkillSet, category: SkillCategory) => {
    if (!category.validateFlag) {
      return
    }
    await validateUserSkillAssessment(userId, skill.id, !skill.isValidated)
    skillAssessmentStore.getSkillSets(userId)
  }

  // Validate all unvalidated skills for the current role
  const validateAll = async (category: SkillCategory) => {
    if (!category.validateFlag) {
      return
    }
    await validateAllUserSkills(userId, category.id, true)
    await skillAssessmentStore.getSkillSets(userId)
  }

  // Reset a single skill assessment, only allowed for skills from the current role
  const resetSkill = async (skill: SkillSet, category: SkillCategory) => {
    if (!category.validateFlag) {
      return
    }
    await resetUserSkill(userId, skill.id)
    await skillAssessmentStore.getSkillSets(userId)
  }
  // Reset all skill assessments for the current role
  const resetAll = async (category: SkillCategory) => {
    if (!category.validateFlag) {
      return
    }
    await resetAllUserSkills(userId, category.id)
    await skillAssessmentStore.getSkillSets(userId)
  }

  const totalSkillsCount = skillAssessmentStore.skillSets?.reduce((acc, category) => acc + (category.skills?.length || 0), 0) || 0

  return (
    <AppPaper sx={styles.root}>
      {!isLoading && skillAssessmentStore.skillSets && skillAssessmentStore.skillSets.length > 0 && checkIfAllSkillsAreRated() && <>
        <AppBox gap={10} justifyContent="space-between" alignItems="center" width="100%">
          <AppBox alignItems="center" gap={5}>
            <Typography variant='h5'>Skills</Typography>
            <Word sx={styles.pill}>{totalSkillsCount}</Word>
          </AppBox>
          <AppBox sx={{ display: 'flex', gap: 15, alignItems: 'center' }}>
            {validatableCategory && (
              <AppButton
                fontSize={10}
                sx={{
                  color: `${Palette.WHITE} !important`,
                  border: `0.5px solid ${Palette.SMOKY_SLATE} !important`,
                  backgroundColor: `${Palette.SMOKY_SLATE} !important`,
                }}
                textValue={translations.commonWords.resetAll()}
                variant={'contained'}
                marginRight={0}
                onClick={() => resetAll(validatableCategory)}
              />
            )}
            {validatableCategory && (
              <AppButton
                fontSize={10}
                textValue={translations.commonWords.validateAll()}
                variant={'light'}
                marginRight={5}
                onClick={() => validateAll(validatableCategory)}
              />
            )}
          </AppBox>
        </AppBox>
        <Typography sx={{ fontSize: 12, color: 'text.secondary' }}>{translations.skillAndGapAnalysisDesc()}</Typography>
      </>}
      {!isLoading && skillAssessmentStore.skillSets && skillAssessmentStore.skillSets.length > 0 && !checkIfAllSkillsAreRated() && <Caption sx={{ color: 'secondary.main' }}>{translations.userNotRatedSkills()}</Caption>}
      {!isLoading && (!skillAssessmentStore.skillSets || skillAssessmentStore.skillSets.length === 0) && <>
        <Caption sx={{ color: 'secondary.main' }}>{translations.managerNotSetSkills()}</Caption>
        <Caption sx={{ color: 'text.secondary', fontSize: 11 }}>{translation.noData()}</Caption>
      </>}
      <AppBox
        flexDirection="column"
        gap={10}
        overflow="auto"
        width="100%"
      >
        {!isLoading && skillAssessmentStore.skillSets?.flatMap(category => (category.skills || []).map((skill: SkillSet) => (
          <AppBox key={skill.id} flexDirection="column" gap={10}>
            <AppBox gap={10} alignItems="center" justifyContent="space-between">
              <AppBox gap={5} alignItems="center">
                <Typography sx={styles.ratingWrapper} fontSize={10}>
                  {skill.selectedSetLevel || 0}/{skill.expected_set_level || 0}
                </Typography>
                <Typography fontSize={12}>{skill.setName}</Typography>
              </AppBox>
              {skill.selectedSetLevel !== null && (
                <AppBox sx={{ display: 'flex', gap: 15, alignItems: 'center' }}>
                  {skill.selectedSetLevel !== null && (
                    <Tooltip title={!category.validateFlag ? translations.errorMsg.noPermissionOtherRoles() : ''} arrow>
                      <AppBox>
                        <AppButton
                          fontSize={10}
                          sx={{
                            color: `${Palette.WHITE} !important`,
                            border: `0.5px solid ${Palette.SMOKY_SLATE} !important`,
                            backgroundColor: `${Palette.SMOKY_SLATE} !important`,
                            opacity: !category.validateFlag ? 0.5 : 1,
                          }}
                          textValue={translations.commonWords.reset()}
                          variant={'contained'}
                          marginRight={0}
                          onClick={() => resetSkill(skill, category)}
                          disabled={!category.validateFlag}
                        />
                      </AppBox>
                    </Tooltip>
                  )}
                  <Tooltip title={!category.validateFlag ? translations.errorMsg.noPermissionOtherRoles() : ''} arrow>
                    <AppBox>
                      <AppButton
                        fontSize={10}
                        sx={skill.isValidated ? {
                          color: `${Palette.WHITE} !important`,
                          border: `0.5px solid ${Palette.DANGER} !important`,
                          backgroundColor: `${Palette.DANGER} !important`,
                          opacity: !category.validateFlag ? 0.5 : 1,
                        } : {
                          opacity: !category.validateFlag ? 0.5 : 1,
                        }}
                        textValue={skill.isValidated ? translations.commonWords.revoke() : translations.commonWords.accept()}
                        variant={skill.isValidated ? 'contained' : 'light'}
                        marginRight={5}
                        onClick={() => validate(skill, category)}
                        disabled={!category.validateFlag}
                      />
                    </AppBox>
                  </Tooltip>
                </AppBox>
              )}
            </AppBox>
            <AppBox gap={5} alignItems="center">
              {[...Array(5)].map((_, index) => (
                <AppBox
                  key={generateUniqId()}
                  sx={{
                    ...styles.box,
                    ...getSegmentColor(skill.selectedSetLevel, skill.expected_set_level, index + 1, skill.isValidated),
                  }}
                />
              ))}
              {(skill.selectedSetLevel !== null && skill.expected_set_level !== 0) && skill.isValidated && (
                <Fade in timeout={800} mountOnEnter unmountOnExit>
                  <Tooltip title={translation.pages.myTeam.sortHeader.validated()}>
                    <VerifiedRoundedIcon fontSize='small' sx={{ color: Palette.GOLDEN_ORANGE }} />
                  </Tooltip>
                </Fade>
              )}
              {(skill.selectedSetLevel !== null && skill.expected_set_level !== 0) && ((skill.selectedSetLevel ?? 0) > (skill.expected_set_level ?? 0)) && (
                <Fade in timeout={800} mountOnEnter unmountOnExit>
                  <StarsRoundedIcon fontSize='small' sx={{ color: Palette.GOLDEN_ORANGE }} />
                </Fade>
              )}
            </AppBox>
          </AppBox>
        )))}
        {isLoading && (
          <AppSkeleton
            count={5}
            direction="column"
            rects={[
              { width: '20%', height: 15 },
              { width: '90%', height: 15 },
              { width: '90%', height: 15 },
            ]}
          />
        )}
      </AppBox>
    </AppPaper>
  )
})

const styles = {
  root: {
    padding: '10px 15px',
    borderRadius: '12px',
    gap: 10,
    width: '100%',
    border: 0,
  },
  ratingWrapper: {
    backgroundColor: 'background.dark',
    color: 'text.primary',
    border: '1px solid',
    padding: '3px 6px',
    borderRadius: '10px',
  },
  box: {
    height: 15,
    width: '10%',
    borderRadius: '10px',
  },
  pill: {
    width: 25,
    height: 25,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: '999px',
    backgroundColor: ColorTokens.primary10,
    color: ColorTokens.primary500,
  },
}